/**
 * Responder Selector Component
 * Multi-select responders with role checkboxes
 */
import { UserCircle } from 'lucide-react'
import PropTypes from 'prop-types'
import React, { useState, useEffect } from 'react'

import { Badge } from '@/components/elements/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/elements/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { formatUserName } from '@/utils/userHelpers'

export function ResponderSelector({
  availableUsers = [],
  availableRoles = [],
  onRespondersChange,
  value = [],
}) {
  const [selectedResponders, setSelectedResponders] = useState(value)

  useEffect(() => {
    setSelectedResponders(value)
  }, [value])

  const handleUserToggle = (userId) => {
    const isSelected = selectedResponders.some((r) => r.user_id === userId)

    let updated
    if (isSelected) {
      // Remove responder
      updated = selectedResponders.filter((r) => r.user_id !== userId)
    } else {
      // Add responder with empty roles
      const user = availableUsers.find((u) => u.id === userId)
      updated = [
        ...selectedResponders,
        {
          user_id: userId,
          user_name: user ? formatUserName(user) : 'Unknown',
          roles: [],
        },
      ]
    }

    setSelectedResponders(updated)
    onRespondersChange?.(updated)
  }

  const handleRoleToggle = (userId, role) => {
    const updated = selectedResponders.map((responder) => {
      if (responder.user_id === userId) {
        const hasRole = responder.roles.includes(role)
        return {
          ...responder,
          roles: hasRole ? responder.roles.filter((r) => r !== role) : [...responder.roles, role],
        }
      }
      return responder
    })

    setSelectedResponders(updated)
    onRespondersChange?.(updated)
  }

  const isUserSelected = (userId) => selectedResponders.some((r) => r.user_id === userId)

  const getUserRoles = (userId) => {
    const responder = selectedResponders.find((r) => r.user_id === userId)
    return responder?.roles || []
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Responders</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {availableUsers.length === 0 ? (
          <div className="text-muted-foreground py-4 text-center">No users available</div>
        ) : (
          <div className="space-y-4">
            {availableUsers.map((user) => {
              const isSelected = isUserSelected(user.id)
              const userRoles = getUserRoles(user.id)

              return (
                <div key={user.id} className="space-y-3 rounded-lg border p-3 transition-all">
                  {/* User Selection */}
                  <div className="flex items-center space-x-3">
                    <Checkbox
                      id={`user-${user.id}`}
                      checked={isSelected}
                      onCheckedChange={() => handleUserToggle(user.id)}
                    />
                    <Label
                      htmlFor={`user-${user.id}`}
                      className="flex flex-1 cursor-pointer items-center gap-2"
                    >
                      <UserCircle className="text-muted-foreground h-5 w-5" />
                      <span className="font-medium">
                        {user.name || user.username || user.email}
                      </span>
                      {isSelected && userRoles.length > 0 && (
                        <div className="ml-auto flex gap-1">
                          {userRoles.map((role) => (
                            <Badge key={role} variant="secondary" className="text-xs">
                              {role}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </Label>
                  </div>

                  {/* Role Selection */}
                  {isSelected && availableRoles.length > 0 && (
                    <div className="ml-8 space-y-2 border-l-2 pl-4">
                      <div className="text-muted-foreground mb-2 text-sm font-medium">Roles:</div>
                      <div className="grid grid-cols-2 gap-2">
                        {availableRoles.map((role) => (
                          <div key={role} className="flex items-center space-x-2">
                            <Checkbox
                              id={`role-${user.id}-${role}`}
                              checked={userRoles.includes(role)}
                              onCheckedChange={() => handleRoleToggle(user.id, role)}
                            />
                            <Label
                              htmlFor={`role-${user.id}-${role}`}
                              className="cursor-pointer text-sm"
                            >
                              {role}
                            </Label>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}

        {selectedResponders.length > 0 && (
          <div className="border-t pt-2">
            <div className="text-muted-foreground text-sm">
              {selectedResponders.length} responder{selectedResponders.length !== 1 ? 's' : ''}{' '}
              selected
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

ResponderSelector.propTypes = {
  availableUsers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string,
      username: PropTypes.string,
      email: PropTypes.string,
    }),
  ),
  availableRoles: PropTypes.arrayOf(PropTypes.string),
  onRespondersChange: PropTypes.func.isRequired,
  value: PropTypes.arrayOf(
    PropTypes.shape({
      user_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      user_name: PropTypes.string.isRequired,
      roles: PropTypes.arrayOf(PropTypes.string).isRequired,
    }),
  ),
}
